import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DataApiResponse,
  DataApiResponseLegacy,
  Distributor,
  MovieAiGenerateRequest,
  MovieAiGenerateResponse,
  MovieAiSettings,
  MovieShow,
  MovieShowMovie,
  MovieShowWeek,
  ShopMovie,
  ShopMovieFormat,
  ShopMovieFormats,
  ShopMovieFormatSaveRequest,
  ShopMovieValidation,
} from '@digilize/shared/definitions/src';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MovieApiService {
  constructor(private http: HttpClient) {}

  // Movies

  getMovie = (id) => this.http.get(`/api/v1.0/movies/${id}`);

  getAvailableMovies = (params) => this.http.get('/api/v1.0/movies?movie_statuses=2', { params });

  getShopUnassignedMovies = (params) => this.http.get(`/api/v1.0/movies/unassigned?movie_statuses=2`, { params });

  resyncShopMovie = (id) => this.http.post<ShopMovie>(`/api/v1.0/shop-movies/${id}/resync`, {});

  shopMoviesBulkAction = (body) => this.http.post('/api/v1.0/shop-movies/bulk', body);

  getPeople = (params) => this.http.get('/api/v1.0/movies/people/autocomplete', { params: params });

  getGenres = () => this.http.get<string[]>('/api/v1.0/movies/genres');

  importMovieByFilmdepotId = (globalId: string) =>
    this.http.post<MovieImportResult>('/api/v1.0/movies/import', { global_id: globalId });

  // Distributors

  getDistributors = (params) =>
    this.http.get<DataApiResponse<Distributor[]>>('/api/v1.0/distributors', { params: params });

  // Shop

  getShopMovie = (id) => this.http.get<ShopMovie>(`/api/v1.0/shop-movies/${id}`);

  getShopMovies = (params) => this.http.get<DataApiResponse<ShopMovie[]>>('/api/v1.0/shop-movies', { params: params });

  // Film formats (PAUL-3131): saved one by one, never through the film's own save

  getShopMovieFormats = (shopMovieId: string) =>
    this.http.get<ShopMovieFormats>(`/api/v1.0/shop-movies/${shopMovieId}/formats`);

  addShopMovieFormat = (shopMovieId: string, body: ShopMovieFormatSaveRequest) =>
    this.http.post<ShopMovieFormat>(`/api/v1.0/shop-movies/${shopMovieId}/formats`, body);

  editShopMovieFormat = (shopMovieId: string, formatId: string, body: ShopMovieFormatSaveRequest) =>
    this.http.put<ShopMovieFormat>(`/api/v1.0/shop-movies/${shopMovieId}/formats/${formatId}`, body);

  removeShopMovieFormat = (shopMovieId: string, formatId: string) =>
    this.http.delete<void>(`/api/v1.0/shop-movies/${shopMovieId}/formats/${formatId}`);

  // AI

  generateMovieDescription = (body: MovieAiGenerateRequest): Observable<MovieAiGenerateResponse> =>
    this.http.post<MovieAiGenerateResponse>('/api/v1.0/shop-movies/ai-generate-description', body);

  getMovieAiSettings = (companyId: string): Observable<MovieAiSettings> =>
    this.http.get<MovieAiSettings>(`/api/v1.0/movies/ai-settings/${companyId}`);

  editMovieAiSettings = (companyId: string, body: MovieAiSettings): Observable<MovieAiSettings> =>
    this.http.put<MovieAiSettings>(`/api/v1.0/movies/ai-settings/${companyId}`, body);

  // Shows

  importShows = () => this.http.post('/api/v1.0/shows/import', {});

  getShows = (params) => this.http.get<DataApiResponseLegacy<MovieShow[]>>('/api/v1.0/shows', { params: params });

  getShowMovies = (params) =>
    this.http.get<DataApiResponseLegacy<MovieShowMovie[]>>('/api/v1.0/shows/movies', { params: params });

  /** The `getShows` filters plus a required `from`/`to`, counted per play week (PAUL-3850). */
  getShowWeeks = (params) => this.http.get<MovieShowWeek[]>('/api/v1.0/shows/weeks', { params: params });

  getSchedule = (params) => this.http.get<MovieShow[]>('/api/v1.0/shows/schedule', { params });

  removeShows = (body) => this.http.post('/api/v1.0/shows/bulk', body);

  setShowsStatusToImported = (showIds, shopId) =>
    this.http.post('/api/v1.0/shows/bulk', { ids: showIds, action: 'status-Imported', filter: { shop: shopId } });

  setShowsStatusToPublished = (body) => this.http.post('/api/v1.0/shows/bulk', body);

  addShopMovie = (movie) => this.http.post('/api/v1.0/shop-movies', movie);

  activateShopMovie = (body: ShopMovieActivateRequest) =>
    this.http.post<ShopMovie>('/api/v1.0/shop-movies/activate', body);

  editShopMovie = (movie) => this.http.put<ShopMovie>('/api/v1.0/shop-movies', movie);

  validateShopMovie = (movie) => this.http.post<ShopMovieValidation>('/api/v1.0/shop-movies/validate', movie);

  editShopMoviePlanner = (moviePlannerEditPayload: MoviePlannerEditPayload) =>
    this.http.put<ShopMovie>(`/api/v1.0/shop-movies/${moviePlannerEditPayload.id}/planner`, moviePlannerEditPayload);

  updateShopMovieStatus = (body) => this.http.post('/api/v1.0/shop-movies/bulk', body);

  removeShopMovie = (shopMovieId: string) => this.http.delete(`/api/v1.0/shop-movies/${shopMovieId}`);

  publishShows = (body) => this.http.post('/api/v1.0/shows/publish', body);

  unpublishShows = (body) => this.http.post('/api/v1.0/shows/unpublish', body);

  addPricingTagsToShows = (body: { movie_show_ids: string[]; product_pricing_tag_ids: string[] }) =>
    this.http.post<DataApiResponseLegacy<MovieShow[]>>('/api/v1.0/shows/product-pricing-tags/attach', body);

  removePricingTagsFromShows = (body: { movie_show_ids: string[]; product_pricing_tag_ids: string[] }) =>
    this.http.post<DataApiResponseLegacy<MovieShow[]>>('/api/v1.0/shows/product-pricing-tags/detach', body);

  addProductsToShows = (body: { movie_show_ids: string[]; product_ids: string[] }): Observable<void> =>
    this.http.post<void>('/api/v1.0/shows/products/attach', body);

  removeProductsFromShows = (body: { movie_show_ids: string[]; product_ids: string[] }): Observable<void> =>
    this.http.post<void>('/api/v1.0/shows/products/detach', body);

  editBulkProductsFromShows = (body: { movie_show_ids: string[]; product_ids: string[] }): Observable<void> => {
    return this.http.post<void>('/api/v1.0/shows/products', body);
  };

  updateTagsAndProducts = (body: { movie_show_ids: string[] }) =>
    this.http.post('/api/v1.0/shows/product-pricing-tags/link', body);

  exportCinewriter = (params) =>
    this.http.get('/api/v1.0/shows/export-cinewriter', { params: params, responseType: 'blob' });

  switchShowMovie = (showId: string, body: SwitchShowMovieRequest) =>
    this.http.post<MovieShow>(`/api/v1.0/shows/${showId}/switch-movie`, body);

  // Events

  getEvent = (id) => this.http.get(`/api/v1.0/events/${id}`);

  getEvents = (params) => this.http.get('/api/v1.0/events', { params });

  addEvent = (body) => this.http.post('/api/v1.0/events', body);

  editEvent = (body) => this.http.put('/api/v1.0/events', body);

  removeEvent = (id) => this.http.delete(`/api/v1.0/events/${id}`);

  inviteUserToAnEvent = (eventId, body) => this.http.post(`/api/v1.0/events/${eventId}/users`, body);

  resendInvite = (inviteId) => this.http.post(`/api/v1.0/events/invite/${inviteId}/resend`, {});

  getEventUsers = (eventId, params) => this.http.get(`/api/v1.0/events/${eventId}/users`, { params });

  removeEventUser = (eventId, userId) => this.http.delete(`/api/v1.0/events/${eventId}/users/${userId}`);

  removeInvitation = (eventId, userEmail) =>
    this.http.delete(`/api/v1.0/events/${eventId}/invite`, { params: { email: userEmail } });

  getInvitationPreview = (eventId, body) =>
    this.http.post(`/api/v1.0/events/${eventId}/invite/preview`, body, { responseType: 'text' });

  bulkEventInviteAction = (body) => this.http.post(`/api/v1.0/events/invite/bulk`, body);

  exportInvites = (body) => this.http.post(`/api/v1.0/events/invite/export`, body, { responseType: 'text' });

  // Quizzes

  getUnassignedQuizzes = (params) => this.http.get('/api/v1.0/quizzes/unassigned', { params });

  getQuiz = (quizId) => this.http.get(`/api/v1.0/quizzes/${quizId}`);

  getShopQuizzes = (params) => this.http.get('/api/v1.0/shop-quizzes', { params });

  getShopQuiz = (quizId) => this.http.get(`/api/v1.0/shop-quizzes/${quizId}`);

  assignQuiz = (quiz) => this.http.post(`/api/v1.0/shop-quizzes`, quiz);

  editShopQuiz = (quiz) => this.http.put(`/api/v1.0/shop-quizzes`, quiz);

  removeShopQuiz = (params) => this.http.get('/api/v1.0/shop-quizzes', { params });
  updateShopQuizStatus = (params) => this.http.get('/api/v1.0/shop-quizzes', { params });
}

// Only these fields are applied by the backend planner edit (UpdaterPlanner); anything else is ignored.
export interface MoviePlannerEditPayload {
  id: string;
  distributor_notes?: string;
  credits_start_time_in_minutes?: number;
  color?: string;
}

export interface MovieImportResult {
  id: string;
  existed: boolean;
  new: boolean;
}

export interface ShopMovieActivateRequest {
  shop_id: string;
  movie_id: string;
  /**
   * PAUL-3744: the film's Filmdepot versions the cinema shows it in. Left out or empty activates the main
   * format only. The main version stays main when chosen, otherwise the first chosen one becomes main.
   */
  movie_format_ids?: string[];
}

/**
 * A film switch (`movie_id` or `shop_movie_id`) or a format switch (`shop_movie_format_id` alone). A new film
 * without a format takes that film's main format. `dry_run` checks the new runtime fits without saving.
 */
export interface SwitchShowMovieRequest {
  movie_id?: string;
  shop_movie_id?: string;
  shop_movie_format_id?: string;
  dry_run?: boolean;
}
